/**
 * Send Run Button Component — with adjustable count
 */

import { useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../services/firebase';
import { PlatformFeeNotificationModal } from './PlatformFeeNotificationModal';
import { SendRunConfirmModal } from './SendRunConfirmModal';
import { isSendOutcomeUnknown, SEND_WAIT_MS, sendUnconfirmedMessage } from './sendOutcome';
import { notifyLeadsChanged } from '../../hooks/leadRunCounts';

interface SendRunButtonProps {
  runNumber: number;
  /**
   * Leads ready to call right now (status='pending' AND nextEligibleCallAt
   * is null OR has already passed). This is what the dispatcher picks up,
   * so the chip shows it instead of total-pending and avoids the "500
   * available" → "no eligible leads" confusion.
   */
  available: number;
  /**
   * Pending leads still inside their recall waiting window. Not callable
   * yet — surfaced as a sub-line so the user knows why Run N shows fewer
   * ready than they expect.
   */
  waiting?: number;
  /** Earliest unlock time across waiting leads (ms epoch). null when waiting === 0. */
  earliestEligibleMs?: number | null;
  disabled?: boolean;
  clientId?: string | null; // Admin impersonation
  balance: number;           // Call-credit balance (per_call billing)
  billingType: 'per_call' | 'per_contact';
  onBuyPlatform?: () => void; // Opens BuyCredits panel when user clicks "Pay platform fee"
  // Per-client cap from settings.maxCallsPerRun. Defaults to 2000 for clients
  // without an explicit setting. The hard server ceiling is 5000 (enforced in
  // dispatcher.ts MAX_LEADS_PER_RUN); we clamp here so the input never offers
  // more than what the server will accept.
  maxPerRun?: number;
}

/**
 * Friendly ETA for the earliest waiting lead. "ready in 3 days", "ready
 * in 1 day", "ready later today". Used under the chip when waiting > 0.
 */
function formatEligibleEta(ms: number | null | undefined): string {
  if (ms == null) return '';
  const diffMs = ms - Date.now();
  if (diffMs <= 0) return 'ready now';
  // Same local calendar day — Math.ceil below is always >= 1 for any future time.
  if (new Date(ms).toDateString() === new Date().toDateString()) return 'ready later today';
  const days = Math.ceil(diffMs / (24 * 60 * 60 * 1000));
  if (days === 1) return 'ready in 1 day';
  return `ready in ${days} days`;
}

const DEFAULT_MIN_PER_RUN = 100;
const DEFAULT_MAX_PER_RUN = 2000;
const HARD_CEILING_PER_RUN = 5000;

export function SendRunButton({
  runNumber,
  available,
  waiting = 0,
  earliestEligibleMs = null,
  disabled,
  clientId,
  balance,
  billingType,
  onBuyPlatform,
  maxPerRun,
}: SendRunButtonProps) {
  const effectiveMaxPerRun = Math.min(
    HARD_CEILING_PER_RUN,
    Math.max(1, maxPerRun ?? DEFAULT_MAX_PER_RUN),
  );
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState<string | null>(null);
  // No clear answer reached the browser: the send may have gone through, so not a red error.
  const [unconfirmed, setUnconfirmed] = useState(false);
  const [customCount, setCustomCount] = useState<string>('');
  const [showPlatformFeeModal, setShowPlatformFeeModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingCount, setPendingCount] = useState<number>(0);

  // Effective minimum drops below DEFAULT_MIN_PER_RUN when either:
  //   (A) per_call billing AND call credits < DEFAULT_MIN_PER_RUN
  //   (B) available eligible leads for this run < DEFAULT_MIN_PER_RUN
  // Floor of 1 (can't send zero calls).
  const creditFloor = billingType === 'per_call'
    ? Math.max(1, balance)
    : DEFAULT_MIN_PER_RUN;
  const availableFloor = Math.max(1, available);
  const effectiveMin = Math.min(DEFAULT_MIN_PER_RUN, availableFloor, creditFloor);
  const effectiveMax = Math.min(available, effectiveMaxPerRun);
  const isReducedMin = effectiveMin < DEFAULT_MIN_PER_RUN;

  // Click handler: compute the count, open the confirm modal. The modal
  // calls back into `handleConfirmedSend` once the user picks a path
  // (all-eligible or by-file).
  const handleSend = () => {
    if (available === 0 || disabled) return;

    const requested = customCount
      ? parseInt(customCount)
      : Math.min(available, effectiveMaxPerRun);
    const count = Math.min(
      Math.max(requested || effectiveMin, effectiveMin),
      available,
      effectiveMaxPerRun,
    );
    setPendingCount(count);
    setShowConfirmModal(true);
  };

  const handleConfirmedSend = async ({ sourceFiles, count }: { sourceFiles?: string[]; count?: number }) => {
    setShowConfirmModal(false);
    setSending(true);
    setResult(null);
    setUnconfirmed(false);

    try {
      const sendRun = httpsCallable(functions, 'sendRun', { timeout: SEND_WAIT_MS });
      const response = await sendRun({
        runNumber,
        count: count ?? pendingCount,
        ...(sourceFiles && sourceFiles.length > 0 ? { sourceFile: sourceFiles } : {}),
        ...(clientId ? { clientId } : {}),
      });
      const data = response.data as { message: string };
      setResult(data.message);
      setCustomCount('');
    } catch (error) {
      // sendRun throws HttpsError('failed-precondition', msg, { code: 'platform_fee_required' })
      // when the client has no active platform fee — open the dedicated modal instead
      // of just showing a tiny red error string.
      const details = (error as { details?: { code?: string } })?.details;
      if (details?.code === 'platform_fee_required') {
        setShowPlatformFeeModal(true);
      } else if (isSendOutcomeUnknown(error)) {
        setUnconfirmed(true);
        setResult(sendUnconfirmedMessage());
      } else {
        const message = error instanceof Error ? error.message : 'Unknown error';
        setResult('Error: ' + message);
      }
    } finally {
      setSending(false);
      notifyLeadsChanged();
    }
  };

  const isDisabled = disabled || sending || available === 0;

  return (
    <div>
      <button
        onClick={handleSend}
        disabled={isDisabled}
        className={`run-btn ${isDisabled ? 'disabled' : 'active'}`}
        style={{ width: '100%' }}
        title={waiting > 0
          ? `${available.toLocaleString()} ready · ${waiting.toLocaleString()} still in recall window (${formatEligibleEta(earliestEligibleMs)})`
          : undefined}
      >
        <div style={{ fontWeight: 600 }}>Run {runNumber}</div>
        <div style={{ fontSize: '0.875rem', color: 'var(--gray-400)' }}>
          {available} available
        </div>
        {waiting > 0 && (
          <div style={{
            fontSize: '0.7rem',
            color: 'var(--gray-500)',
            marginTop: '2px',
          }}>
            +{waiting.toLocaleString()} in recall · {formatEligibleEta(earliestEligibleMs)}
          </div>
        )}
      </button>
      {available > 0 && !isDisabled && (
        <>
          <input
            type="number"
            min={effectiveMin}
            max={effectiveMax}
            placeholder={`${effectiveMin} - ${effectiveMax}`}
            value={customCount}
            onChange={e => setCustomCount(e.target.value)}
            style={{
              width: '100%',
              marginTop: '6px',
              padding: '6px 8px',
              fontSize: '0.8rem',
              background: 'var(--dark)',
              border: '1px solid var(--dark-3)',
              borderRadius: '4px',
              color: 'var(--white)',
              fontFamily: 'inherit',
              textAlign: 'center',
            }}
          />
          {isReducedMin && (
            <div style={{
              fontSize: '0.7rem',
              color: 'var(--gray-500)',
              marginTop: '2px',
              textAlign: 'center',
            }}>
              Limited batch: fewer than {DEFAULT_MIN_PER_RUN} {available < DEFAULT_MIN_PER_RUN ? 'leads' : 'credits'} available
            </div>
          )}
        </>
      )}
      {sending && (
        <div style={{ fontSize: '0.75rem', color: 'var(--gold)', marginTop: '4px' }}>
          Sending...
        </div>
      )}
      {result && (
        <div
          style={{
            fontSize: '0.75rem',
            color: unconfirmed ? 'var(--gold)' : result.startsWith('Error') ? 'var(--red)' : 'var(--green)',
            marginTop: '4px'
          }}
        >
          {result}
        </div>
      )}
      {showPlatformFeeModal && (
        <PlatformFeeNotificationModal
          source="send_attempt"
          onClose={() => setShowPlatformFeeModal(false)}
          onBuyPlatform={onBuyPlatform ? () => {
            setShowPlatformFeeModal(false);
            onBuyPlatform();
          } : undefined}
        />
      )}
      <SendRunConfirmModal
        isOpen={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        onConfirm={handleConfirmedSend}
        runNumber={runNumber}
        count={pendingCount}
        clientId={clientId}
        billingType={billingType}
        balance={balance}
      />
    </div>
  );
}
